import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bookmark, ChevronDown, Lock, Pencil, Save, Trash2, Users, X } from 'lucide-react';
import toast from 'react-hot-toast';
import ModalPortal from '../UI/ModalPortal';
import ConfirmDialog from '../UI/ConfirmDialog';
import useSavedFilters from '../../hooks/useSavedFilters';
import { TextField, Checkbox } from '@anyemp/ui-kit';
import ModalFrame from '../UI/ModalFrame';
import { getAnchoredOverlayStyle, isRectOutsideViewport } from '../../utils/viewportPosition';

function pickKeys(obj, allowedKeys) {
  if (!obj || !allowedKeys?.length) return {};
  const set = new Set(allowedKeys);
  return Object.fromEntries(
    Object.entries(obj).filter(([k]) => set.has(k))
  );
}

const SavedFiltersDropdown = ({
  pageKey,
  currentFilters,
  applyKeys,
  onApply,
  className = '',
}) => {
  const { items, loading, save, update, remove } = useSavedFilters(pageKey);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [saveModal, setSaveModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState(null);

  const activeItem = useMemo(
    () => items.find((it) => it.id === activeId) || null,
    [items, activeId]
  );

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)
        && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const updateDropdownPosition = useCallback(() => {
    if (!open || !wrapRef.current) return;
    const anchorRect = wrapRef.current.getBoundingClientRect();
    const viewport = { viewportWidth: window.innerWidth, viewportHeight: window.innerHeight };
    if (isRectOutsideViewport(anchorRect, viewport)) {
      setOpen(false);
      return;
    }
    setDropdownStyle(getAnchoredOverlayStyle({
      anchorRect,
      ...viewport,
      preferredWidth: 288,
      preferredHeight: 360,
      align: 'end',
    }));
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    updateDropdownPosition();
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [open, updateDropdownPosition]);

  const applyItem = (item) => {
    const sanitized = pickKeys(item.filters, applyKeys);
    setActiveId(item.id);
    setOpen(false);
    onApply?.(sanitized);
  };

  const handleSave = async (name, visibility) => {
    try {
      const payload = pickKeys(currentFilters, applyKeys);
      const created = await save(name.trim(), payload, visibility);
      if (created?.id) setActiveId(created.id);
      toast.success('Filter saved');
      setSaveModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to save filter');
    }
  };

  const handleOverwrite = async () => {
    if (!activeItem || !activeItem.is_owner) return;
    try {
      const payload = pickKeys(currentFilters, applyKeys);
      await update(activeItem.id, { filters: payload });
      toast.success(`Updated "${activeItem.name}"`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to update filter');
    }
  };

  const handleEdit = async (name, visibility) => {
    if (!editModal) return;
    try {
      await update(editModal.id, { name: name.trim(), visibility });
      toast.success('Filter updated');
      setEditModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to update filter');
    }
  };

  const handleDelete = async (item) => {
    setDeleting(true);
    try {
      await remove(item.id);
      if (activeId === item.id) setActiveId(null);
      toast.success('Filter deleted');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to delete filter');
    } finally {
      setDeleting(false);
    }
  };

  const triggerLabel = activeItem?.name || 'Saved filters';

  return (
    <div ref={wrapRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex min-h-10 items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      >
        <Bookmark className="h-4 w-4" />
        <span className="max-w-[160px] truncate">{triggerLabel}</span>
        <ChevronDown className="h-4 w-4 opacity-60" />
      </button>

      {open && dropdownStyle && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="flex min-h-0 flex-col overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loading && (
              <div className="px-3 py-2 text-xs text-gray-500 dark:text-gray-400">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
                No saved filters yet
              </div>
            )}
            {items.map((item) => {
              const isActive = item.id === activeId;
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${
                    isActive
                      ? 'bg-indigo-50 dark:bg-indigo-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                  onClick={() => applyItem(item)}
                >
                  {item.visibility === 'shared' ? (
                    <Users className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" title="Shared" />
                  ) : (
                    <Lock className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" title="Private" />
                  )}
                  <span className="flex-1 truncate text-gray-800 dark:text-gray-200">{item.name}</span>
                  {!item.is_owner && (
                    <span className="text-[10px] uppercase tracking-wide text-gray-400">shared</span>
                  )}
                  {item.is_owner && (
                    <>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditModal({ id: item.id, name: item.name, visibility: item.visibility });
                        }}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-indigo-500 dark:hover:bg-gray-700"
                        title="Rename / change visibility"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(item);
                        }}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-red-500 dark:hover:bg-gray-700"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 p-2 flex flex-col gap-1">
            <button
              type="button"
              className="w-full text-left text-sm px-2 py-1.5 rounded text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-gray-700 inline-flex items-center gap-2"
              onClick={() => setSaveModal({ name: '', visibility: 'private' })}
            >
              <Save className="h-4 w-4" />
              Save current as…
            </button>
            {activeItem?.is_owner && (
              <button
                type="button"
                className="w-full text-left text-sm px-2 py-1.5 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={handleOverwrite}
              >
                Overwrite "{activeItem.name}"
              </button>
            )}
            {activeItem && (
              <button
                type="button"
                className="w-full text-left text-sm px-2 py-1.5 rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                onClick={() => {
                  setActiveId(null);
                  setOpen(false);
                }}
              >
                Clear selection
              </button>
            )}
          </div>
        </div>,
        document.body
      )}

      {saveModal && (
        <SaveModal
          title="Save filter"
          initial={saveModal}
          existingNames={items.filter((it) => it.is_owner).map((it) => it.name)}
          onClose={() => setSaveModal(null)}
          onConfirm={handleSave}
        />
      )}

      {editModal && (
        <SaveModal
          title="Edit filter"
          submitLabel="Save changes"
          initial={editModal}
          existingNames={items
            .filter((it) => it.is_owner && it.id !== editModal.id)
            .map((it) => it.name)}
          onClose={() => setEditModal(null)}
          onConfirm={handleEdit}
        />
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => handleDelete(deleteTarget)}
        title="Delete Saved Filter"
        message={<>Delete saved filter <strong>{deleteTarget?.name}</strong>?</>}
        confirmText="Delete"
        confirmStyle="danger"
        isLoading={deleting}
      />
    </div>
  );
};

const SaveModal = ({ title = 'Save filter', submitLabel = 'Save', initial, existingNames, onClose, onConfirm }) => {
  const [name, setName] = useState(initial.name || '');
  const [visibility, setVisibility] = useState(initial.visibility || 'private');
  const [saving, setSaving] = useState(false);

  const trimmed = name.trim();
  const duplicate = !!trimmed && existingNames.includes(trimmed);
  const canSave = trimmed.length > 0 && trimmed.length <= 120 && !duplicate;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await onConfirm(trimmed, visibility);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal onClose={saving ? undefined : onClose} ariaLabel={title}>
      <ModalFrame size="sm" className="max-w-sm dark:bg-gray-800">
        <form onSubmit={handleSubmit} className="contents">
        <ModalFrame.Header className="items-center p-4 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            disabled={saving}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </ModalFrame.Header>
        <ModalFrame.Body className="space-y-3 p-4">
          <div>
            <TextField
              label="Name"
              size="small"
              fullWidth
              autoFocus
              type="text"
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
              placeholder="E.g. Active candidates"
              error={!!duplicate}
              helperText={duplicate ? 'You already have a filter with this name on this page' : undefined}
            />
          </div>
          <Checkbox
            label={(
              <span>
                Share with everyone
                <span className="block text-xs text-gray-500 dark:text-gray-400">
                  Visible to all talent-service users
                </span>
              </span>
            )}
            color="primary"
            size="small"
            checked={visibility === 'shared'}
            onChange={(checked) => setVisibility(checked ? 'shared' : 'private')}
          />
        </ModalFrame.Body>
        <ModalFrame.Footer className="flex flex-col-reverse gap-2 p-4 dark:border-gray-700 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSave || saving}
            className="px-3 py-1.5 text-sm rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving && (
              <span className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
            )}
            {submitLabel}
          </button>
        </ModalFrame.Footer>
        </form>
      </ModalFrame>
    </ModalPortal>
  );
};

export default SavedFiltersDropdown;
